'use client'

import { Box, Flex } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import NavChipList, {
  type NavChipListItem,
} from '@/app/[locale]/price/_components/HomepageV2/PredictionSection/components/NavChipList'
import SortMenu from '@/app/[locale]/price/_components/HomepageV2/SortMenu'
import homeV2Styles from '@/app/[locale]/price/_components/HomepageV2/style.module.css'
import type { EquityAssetSort } from '@/services/equity/screener'

import BasketsTable from './Baskets'
import StockTableData from './StockTableData'

type StockSubTab = 'trending' | 'baskets'

/**
 * Trending list sort options, backed by the public assets endpoint's
 * server-side `sort_by`/`sort_direction`. `default` sends no sort params and
 * keeps the upstream most-traded order. 24h volume is intentionally absent —
 * the public API does not support sorting by volume (WWW-5097, 2026-09-09).
 */
const TRENDING_SORT_OPTIONS = [
  { value: 'default', labelKey: 'stockTable.sort.trending', sort: undefined },
  {
    value: 'marketCap',
    labelKey: 'stockTable.sort.marketCap',
    sort: { sortBy: 'market_cap', sortDirection: 'desc' } as EquityAssetSort,
  },
  {
    value: '24hChange',
    labelKey: 'stockTable.sort.24hChange',
    sort: { sortBy: 'performance', sortDirection: 'desc' } as EquityAssetSort,
  },
] as const

type TrendingSortValue = (typeof TRENDING_SORT_OPTIONS)[number]['value']

interface StockTableTabsProps {
  // Passed through to the Trending table (sparkline symbol → asset_id map).
  readonly assetIdBySymbol: Record<string, string>
}

/**
 * Stocks sub-tab pills (WWW-5085): Trending = the existing most-traded stocks
 * table, Baskets = equity basket offers from the public trading-product API.
 *
 * Local state only (no URL sync); switching tabs unmounts the other side
 * (decision 2026-09-07), so Trending pagination state resets on revisit.
 */
export default function StockTableTabs({ assetIdBySymbol }: StockTableTabsProps) {
  const t = useTranslations('stockTable')
  const [activeTab, setActiveTab] = useState<StockSubTab>('trending')
  const [sortValue, setSortValue] = useState<TrendingSortValue>('default')
  const activeSort =
    TRENDING_SORT_OPTIONS.find(option => option.value === sortValue) ?? TRENDING_SORT_OPTIONS[0]

  const chipItems = useMemo<NavChipListItem[]>(
    () => [
      {
        slug: 'trending',
        label: t('tabTrending'),
        isActive: activeTab === 'trending',
        onClick: () => setActiveTab('trending'),
      },
      {
        slug: 'baskets',
        label: t('tabBaskets'),
        isActive: activeTab === 'baskets',
        onClick: () => setActiveTab('baskets'),
      },
    ],
    [t, activeTab],
  )

  return (
    <Box>
      <Flex className={homeV2Styles.controls} mb={16}>
        <Box flex={1} miw={0}>
          <NavChipList items={chipItems} />
        </Box>
        <Box className={homeV2Styles.sortControl}>
          {activeTab === 'trending' && (
            <SortMenu
              options={TRENDING_SORT_OPTIONS}
              activeValue={activeSort.value}
              onChange={value => setSortValue(value as TrendingSortValue)}
            />
          )}
        </Box>
      </Flex>
      {activeTab === 'trending' ? (
        <StockTableData assetIdBySymbol={assetIdBySymbol} sort={activeSort.sort} />
      ) : (
        <BasketsTable />
      )}
    </Box>
  )
}
